import { SessionExpiredError } from '@squire/api-client';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

/**
 * The api client rejects an HTTP error with the response BODY, not an
 * `AxiosError`, and adds the status to it as `statusCode`, which
 * `readStatusCode` reads. This class carries the status for the two schedule
 * queries that capture it themselves through `validateStatus` (SQR-15308).
 */
export class HttpStatusError extends Error {
  readonly status: number;

  readonly body: unknown;

  constructor(status: number, body?: unknown) {
    super(`Request failed with status code ${status}`);
    this.name = 'HttpStatusError';
    this.status = status;
    this.body = body;
    // Keeps `instanceof` working once the class is transpiled down.
    Object.setPrototypeOf(this, HttpStatusError.prototype);
  }
}

/**
 * Converts a resolved error response into an `HttpStatusError`. Pair it with a
 * `validateStatus` that resolves error statuses — see the two schedule queries.
 *
 * That pairing is not offered as a shared request helper on purpose: a
 * `validateStatus` reached on its own makes an error response resolve as though
 * it had succeeded, handing back `{ code, message }` wearing the success type.
 * Other requests get `statusCode` on the rejected body from the api client.
 */
export const throwOnHttpError = <T>(
  response: AxiosResponse<T>,
): AxiosResponse<T> => {
  if (response.status >= 400) {
    throw new HttpStatusError(response.status, response.data);
  }

  return response;
};

/**
 * The HTTP status behind a rejection, whichever shape it arrived in: an
 * `HttpStatusError` from `throwOnHttpError`, or a response body that carries
 * `statusCode` itself. `undefined` means no response ever arrived.
 */
export const readStatusCode = (error: unknown): number | undefined => {
  if (error instanceof HttpStatusError) return error.status;
  if (typeof error !== 'object' || error === null) return undefined;

  const { statusCode } = error as { statusCode?: unknown };
  return typeof statusCode === 'number' ? statusCode : undefined;
};

const MAX_API_RETRIES = 3;

/**
 * A 4xx is the server's verdict on the request as sent — repeating it cannot
 * change the answer, so retrying only multiplies load. A 5xx or a transport
 * failure (no response at all) is worth another attempt.
 */
export const isRetryableHttpError = (error: unknown): boolean => {
  // The session is gone (a failed refresh, or the request interceptor finding
  // it expired), so retrying burns the backoff while the client logs out.
  if (error instanceof SessionExpiredError) return false;

  const status = readStatusCode(error);
  if (status === undefined) return true;

  return status >= 500;
};

/**
 * React Query `retry` predicate: back off a server-side or transport failure,
 * give up immediately on anything the server has already ruled on. Use it
 * directly in queries that declare their own `TError`.
 */
export const shouldRetryApiFailure = (failureCount: number, error: unknown) =>
  isRetryableHttpError(error) && failureCount < MAX_API_RETRIES;

/**
 * `shouldRetryApiFailure` with `error` typed `Error` to match React Query's own
 * signature: it is what the query's `TError` is inferred from. Production
 * rejections are not actually `Error` instances (the interceptor rejects with
 * the response body), which is why `isRetryableHttpError` sniffs the shape.
 */
export const retryOnServerError = (failureCount: number, error: Error) =>
  shouldRetryApiFailure(failureCount, error);

/**
 * No response arrived (timeout, dropped connection, page unload): the api
 * client rejects with an axios error without a response (`undefined` before
 * 1.2.3).
 */
export const isNoResponse = (error: unknown): boolean =>
  error === undefined || (axios.isAxiosError(error) && !error.response);

/**
 * The exact codes squire-api answers when a requested slot or barber is no
 * longer free. Not an `appointment.errors.` prefix: that also covers blocked
 * customers, deposits and waivers.
 */
const SLOT_UNAVAILABLE_CODES = new Set([
  'appointment.errors.nonMatchingHours',
  'appointment.errors.oneOfTimeslotsNotAvailable',
]);

export const isSlotUnavailableError = (error: unknown): boolean => {
  const { code } = (error ?? {}) as { code?: unknown };
  return typeof code === 'string' && SLOT_UNAVAILABLE_CODES.has(code);
};
